import { colorPartsOf } from "@v2/lib/colorParts";
import { CartVariantOptionType } from "@/app/types/cart";
import { ProductDetailType, ProductVariantAxisType } from "@/app/types/product-site";
import { PDPSearchParams } from "./types";

// Priority is the curated order, but more than half the site products were
// migrated with every product left on priority 1. The id falls back to the same
// tie break the API uses, so the variant here matches the one the PLP links to
// and the one the landing sliders show.
export function sortByPriorityAsc<T extends { priority: number, product_id: number }>(items: T[]) {
    return [...items].sort((a, b) => a.priority - b.priority || b.product_id - a.product_id);
}

export function pickShowProduct(
    details: ProductDetailType[],
    searchParams: PDPSearchParams
): ProductDetailType | undefined {
    if (!details.length) return undefined;

    if (searchParams.pid) {
        const pid = Number(searchParams.pid);
        if (!Number.isNaN(pid)) {
            return details.find(p => p.product_id === pid);
        }
    }

    const hasColor = Boolean(searchParams.color_code);
    const hasSize = Boolean(searchParams.size);
    const hasFilters = hasColor || hasSize;

    if (hasFilters) {
        const exact = sortByPriorityAsc(
            details.filter(
                p =>
                    (hasColor ? p.color_code === searchParams.color_code : true) &&
                    (hasSize ? p.size_number === searchParams.size : true)
            )
        )[0];
        if (exact) return exact;

        // Old lens links can carry diameter 70 in size; migration writes the
        // index there. The colour code still identifies the intended variant.
        if (hasColor) {
            return sortByPriorityAsc(details.filter(p => p.color_code === searchParams.color_code))[0];
        }
        return undefined;
    }

    const available = details.filter(p => p.is_available !== false);
    const choices = available.length ? available : details;
    const inStockAndPriced = sortByPriorityAsc(
        choices.filter(p => p.quantity > 0)
    )[0];

    return inStockAndPriced ?? sortByPriorityAsc(choices)[0];
}

/** Pick a real available combination while keeping as many current choices as possible. */
export function variantAxisValueId(product: ProductDetailType, axis: ProductVariantAxisType): number | null {
    return axis.slug === 'color'
        ? product.color_combo_id ?? null
        : product.option_values?.find(value => value.option_type_id === axis.option_type_id)?.option_value_id ?? null;
}

export function pickVariantForAxis(
    details: ProductDetailType[],
    axes: ProductVariantAxisType[],
    active: ProductDetailType,
    axis: ProductVariantAxisType,
    valueId: number,
): ProductDetailType | undefined {
    const matches = (product: ProductDetailType) => axes
        .filter(other => other !== axis && variantAxisValueId(product, other) === variantAxisValueId(active, other)).length;

    return details
        .filter(product => product.is_available !== false && variantAxisValueId(product, axis) === valueId)
        .sort((a, b) => matches(b) - matches(a)
            || Number(b.quantity > 0) - Number(a.quantity > 0)
            || a.priority - b.priority
            || b.product_id - a.product_id)[0];
}

/** Same rule as the buy button: the variant can be bought or inquired right now. */
export function isPurchasable(product: ProductDetailType): boolean {
    return product.is_available !== false && (product.need_inquiry || (product.quantity > 0 && product.final_price > 0));
}

/**
 * The axes the general picker shows, or null when the product still uses the
 * legacy colour and size picker. A lens product keeps its colour axis only
 * when some colour has a swatch.
 */
export function variantPickerAxes(
    showProduct: ProductDetailType,
    details: ProductDetailType[],
    variantAxes: ProductVariantAxisType[],
): ProductVariantAxisType[] | null {
    const isLens = showProduct.product_class?.slug === 'lenses';
    const axes = variantAxes.filter(axis => axis.slug !== 'color' || !isLens
        || axis.values.some(value => colorPartsOf(value).length > 0));
    const migrated = details.length > 0 && details.every(product => product.variant_key != null);
    return migrated && axes.length ? axes : null;
}

/** The variant's axis values as the cart shows them. Colour is left out: the cart shows its swatches. */
export function variantOptionLabels(product: ProductDetailType, axes: ProductVariantAxisType[]): CartVariantOptionType[] {
    return axes.filter(axis => axis.slug !== 'color').flatMap(axis => {
        const id = variantAxisValueId(product, axis);
        const value = axis.values.find(item => item.id === id);
        return value ? [{ slug: axis.slug, title: axis.title, label: value.label }] : [];
    });
}
